"use client";

import { events } from "@/common/constants";
import ShaderObject from "@/mono/components/gl/ShaderObject";
import emitter from "@/mono/lib/emitter";
import {
  aastep,
  cheapAA,
  range,
  roundBorders,
  transformUV,
} from "@/mono/lib/gl/shaderChunks";
import useAnimation from "@/mono/lib/hooks/useAnimation";
import { lerp, map } from "@/mono/lib/math";
import { EASE_CUSTOM_4 } from "@/mono/lib/styles/easing";
import { useGlobalStore } from "@/mono/store/global";
import { useThree } from "@react-three/fiber";
import { getProcessedSrc } from "@/lib/processedSrc";
import { cubicOut } from "mono/lib/gl/shaderChunks";
import { useVideoTexture } from "mono/third-party/drei/VideoTexture";
import { useImageAsTexture } from "mono/third-party/r3f-scroll-rig-custom";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { useTheme } from "styled-components";
import { Color, Vector2 } from "three";
import { CURSOR_LERP } from "../../Cursor";
import NineGLImageOverlay from "./NineGLImageOverlay";

// Note: Three.js LoadingManager is now wired in ClientLayout.js for early initialization

export const fragmentShader = /* glsl */ `
  varying vec2 vUv;
  varying vec2 vImageUV;
  
  uniform sampler2D tMap;
  uniform sampler2D tDepth;
  
  uniform vec2 uSize;
  uniform vec2 uElementSize;

  uniform vec2 uMouse;
  uniform vec2 uResolution;
  
  uniform float uGrayScale;
  
  uniform float uTime;

  uniform float uTransition;
  uniform float uRand;
  uniform float uCursorTransition;
  uniform float uShouldMove;
  uniform vec3 uBackgroundColor;
  uniform float uDarken;

  ${roundBorders}
  ${transformUV}
  ${range}

  ${aastep}
  ${cheapAA}
  ${cubicOut}

  vec3 greyscale(vec3 color, float str) {
    float g = dot(color, vec3(0.299, 0.587, 0.114));
    return mix(color, vec3(g), str);
  }

  void main() {
    vec2 textureUV = vImageUV;

    float a = 0.0;
    float transitionScale = map(uTransition, 0., 1., 2., 1.);
    transitionScale *= map(uCursorTransition, 0., 1., 1., 1.2);
    textureUV = scaleUV(textureUV, vec2(transitionScale), vec2(0.5));

    vec4 color = texture2D(tMap, textureUV);

    float radius = 10.;
    roundedBorder(0.0, radius, vUv, vec2(uElementSize), a);

    vec3 final = color.rgb;
    final = mix(final, final.rgb * 0.75, uCursorTransition);

    float t = uTransition;

    final *= 1. - uDarken;
    final = greyscale(final, uGrayScale);

    a *= t;

    gl_FragColor = vec4(final, a);
  }
`;

const NineGLImageImpl = forwardRef(
  (
    {
      geometry,
      vertexShader,
      scale,
      uniforms: uniformsProps,
      transforms,
      transition = "up",
      imgRef = { current: null },
      tMap = null,
      src: _src,
      isLink = true,
      debug = false,
      isVideo = false,
      video,
      i,
      useHover = true,
      hide = false,
      darken = 0,
      onAnimateInComplete,
      onReady,
      ...props
    },
    ref
  ) => {
    const isMobileLayout = useGlobalStore((s) => s.isMobileLayout);
    const mesh = useRef();
    const group = useRef();
    const random = useMemo(() => Math.random(), []);
    const overlay = useRef();
    const [rot, _rot] = useMemo(() => [new Vector2(), new Vector2()], []);
    // const [isTextureReady, setIsTextureReady] = useState(false);

    const theme = useTheme();

    const uniforms = useMemo(
      () => ({
        tDepth: { value: null },
        tMap: { value: tMap },
        uRand: { value: random },
        uSize: { value: [0, 0] },
        uDarken: { value: darken },
        uGrayScale: { value: 0 },
        uElementSize: { value: new Vector2(0, 0) },
        uImageDimensions: { value: [1, 1] },
        uImageOffset: { value: [0, 0] },
        uImageScale: { value: [1, 1] },
        uCursorTransition: { value: 0 },
        uShouldMove: { value: 0 },
        uTransition: { value: 0 },
        uTime: { value: 0 },
        uBackgroundColor: {
          value: new Color(theme.theme.background).convertLinearToSRGB(),
        },
      }),
      [random, darken, theme.theme.background]
    );

    function resetAndPlayVideo(currentTexture) {
      const el = currentTexture?.source?.data || currentTexture?.image;
      if (!el?.play) return;
      el.currentTime = 0;
      const playPromise = el.play();
      if (playPromise?.catch) {
        playPromise.catch(() => {});
      }
    }

    // const prewarmedTextureUUID = useRef(null);
    // function prewarm(currentTexture) {
    //   const textureUUID = currentTexture.uuid ?? currentTexture.id;
    //   if (textureUUID && prewarmedTextureUUID.current !== textureUUID) {
    //     prewarmedTextureUUID.current = textureUUID;
    //     //const wasVisible = mesh.current?.visible;
    //     // mesh.current?.toggleVisible(true);
    //     if (mesh.current) mesh.current.visible = true;
    //     requestAnimationFrame(() => {
    //       // if (!wasVisible) mesh.current?.toggleVisible(false);
    //       if (mesh.current) mesh.current.visible = false;
    //     });
    //   }
    // }

    // Update texture and dimensions when tMap changes
    useEffect(() => {
      if (!tMap) return;

      const mediaEl = tMap.source?.data || tMap.image;
      const textureWidth =
        mediaEl?.videoWidth || mediaEl?.width || tMap.image?.width || 0;
      const textureHeight =
        mediaEl?.videoHeight || mediaEl?.height || tMap.image?.height || 0;

      if (textureWidth && textureHeight) {
        uniforms.uSize.value = [textureWidth, textureHeight];
        uniforms.uElementSize.value.set(scale.x, scale.y);
        // Precompute image dimensions scale to cover the element in the vertex shader
        const imageRatio = textureWidth / textureHeight;
        const meshAspect = scale.x / scale.y || 1;
        let scaleWidth = 1;
        let scaleHeight = 1;
        if (imageRatio > meshAspect) {
          scaleWidth = imageRatio / meshAspect;
        } else if (imageRatio < meshAspect) {
          scaleHeight = meshAspect / imageRatio;
        }
        uniforms.uImageDimensions.value = [scaleWidth, scaleHeight];
        mesh.current?.set("tMap", tMap);
        // setIsTextureReady(true);
        // Signal ready once we have a texture and dimensions
        try {
          onReady?.();
        } catch (_) {}
      }

      // prewarm(tMap);
    }, [tMap, scale, uniforms]);

    // Animation handlers
    const hasAnimatedInRef = useRef(false);
    const { animateIn: animateInUp, animateOut: animateOutUp } = useAnimation({
      inParams: {
        ease: EASE_CUSTOM_4,
        duration: isMobileLayout ? 1 : 1.4,
        onUpdate: (v) => {
          const negate = random > 0.5 ? 1 : -1;
          const scaleVal = map(v, 0, 1, 0.5, 1);
          group.current?.scale.setScalar(scaleVal);
          mesh.current?.set("uTransition", v);
        },
        onComplete: () => {
          onAnimateInComplete?.();
        },
      },
      outParams: {
        duration: 0,
        onUpdate: (v) => {
          mesh.current?.set("uTransition", v);
        },
      },
    });

    const { animateIn: onZoomImageIn, animateOut: onZoomImageOut } =
      useAnimation({
        inParams: {
          ease: EASE_CUSTOM_4,
          duration: 1,
          onUpdate: (v) => mesh.current?.set("uCursorTransition", v),
        },
        outParams: {
          ease: EASE_CUSTOM_4,
          duration: 1,
          onUpdate: (v) => mesh.current?.set("uCursorTransition", v),
        },
      });

    function handlePointerOver(_, override = false) {
      if (!mesh.current?.isVisible?.() || !useHover || isMobileLayout) return;
      if (!isLink && !override) return;

      emitter.emit(events.CHANGE_DOCUMENT_CURSOR, { type: "pointer" });
      if (!isMobileLayout) onZoomImageIn();
      overlay.current?.animateIn();
    }

    function handlePointerOut(_, override = false) {
      if (!useHover || isMobileLayout) return;
      if (!isLink && !override) return;

      emitter.emit(events.CHANGE_DOCUMENT_CURSOR, { type: "default" });
      emitter.emit(events.CURSOR_HIDE);
      onZoomImageOut();
      overlay.current?.animateOut();
    }

    const animations = useMemo(
      () => ({
        up: { animateIn: animateInUp, animateOut: animateOutUp },
      }),
      [animateInUp, animateOutUp]
    );

    useImperativeHandle(ref, () => ({
      animateIn: ({ debug } = { debug: false }) => {
        if (!mesh.current) return;

        if (debug) {
          console.log("ANIMATE IN NINE GL IMAGE");
        }
        if (hasAnimatedInRef.current) return;
        hasAnimatedInRef.current = true;
        if (debug) {
          console.log("ANIMATE IN NINE GL IMAGE 2");
        }
        resetAndPlayVideo(tMap);
        animations[transition]?.animateIn();
        if (!hide) mesh.current.toggleVisible(true);
      },
      animateOut: () => {
        if (!mesh.current || !mesh.current.isVisible()) return;
        animations[transition].animateOut();
        if (!hide) mesh.current.toggleVisible(false);
        hasAnimatedInRef.current = false;
      },
      shaderObject: mesh.current,
      group: group.current,
      visible: () => mesh.current?.isVisible?.(),
      playVideo: () => resetAndPlayVideo(tMap),
      stopVideo: () => {
        const el = tMap?.source?.data || tMap?.image;
        el?.pause?.();
      },
      seekVideo: (time) => {
        const el = tMap?.source?.data || tMap?.image;
        if (el) el.currentTime = time;
      },
      triggerPointerOver: () => handlePointerOver(null, true),
      triggerPointerOut: () => handlePointerOut(null, true),
      getElementSize: () => scale,
    }));

    function onLoop({ raycaster, mouse, camera }, delta) {
      if (!useHover || !props.inViewport || isMobileLayout) return;

      const { mesh: meshInstance } = mesh.current;
      raycaster.setFromCamera(mouse, camera);

      const intersects = raycaster.intersectObjects([meshInstance], true);

      if (intersects.length > 0) {
        const p = intersects[0].point;
        meshInstance.updateWorldMatrix(true, false);
        const local = meshInstance.worldToLocal(p);
        rot.set(local.x, local.y);
      } else {
        rot.set(0, 0);
      }

      _rot.x = lerp(_rot.x, rot.x, 0.1, delta);
      _rot.y = lerp(_rot.y, rot.y, 0.1, delta);

      const multiplier = 0.25;
      if (group.current)
        group.current.rotation.set(
          _rot.y * -multiplier,
          _rot.x * multiplier,
          0
        );
    }

    const _vertexShader =
      vertexShader ??
      `
        uniform vec2 uImageDimensions;
        uniform vec2 uImageOffset;
        uniform vec2 uImageScale;

        varying vec2 vUv;
        varying vec2 vImageUV;
        varying vec3 vEye;

        ${transformUV}

        void main() {
          vUv = uv;

          // Centered UV scaled by precomputed image dimensions (cover)
          vImageUV = (.5 + (uv - .5) / uImageDimensions);
          vImageUV = translateUV(vImageUV, uImageOffset);
          vImageUV = scaleUV(vImageUV, vec2(uImageScale.x, uImageScale.y), vec2(0.5));

          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          vEye = cameraPosition;
        }
    `;

    const finalUniforms = useMemo(
      () => ({ ...uniforms, ...uniformsProps }),
      [uniforms, uniformsProps]
    );

    return (
      <group ref={group} {...props}>
        <ShaderObject
          ref={mesh}
          name={`NineGLImage-${i}-${_src}`}
          scale={scale}
          geometry={geometry}
          vertexShader={_vertexShader}
          fragmentShader={fragmentShader}
          uniforms={finalUniforms}
          mouseLerpAlpha={CURSOR_LERP}
          useMouse={isLink}
          onPointerOver={handlePointerOver}
          onPointerOut={handlePointerOut}
          onLoop={onLoop}
        />

        {!isMobileLayout && isLink && (
          <NineGLImageOverlay z={50} {...props} scale={scale} ref={overlay} />
        )}
      </group>
    );
  }
);

// Texture provider components
function WithVideoTexture({ src, video, children }) {
  // Validate video data before constructing URLs
  const hasValidStreamingUrl = video?.streamingUrl && typeof video.streamingUrl === "string" && video.streamingUrl.trim() !== "";
  const hasValidMp4Url = video?.mp4Url && typeof video.mp4Url === "string" && video.mp4Url.trim() !== "";
  const hasValidSrc = src && typeof src === "string" && src.trim() !== "";

  // Determine primary URL with validation
  let url;
  if (video) {
    if (hasValidStreamingUrl) {
      url = getProcessedSrc(video.streamingUrl);
    } else if (hasValidMp4Url) {
      url = getProcessedSrc(video.mp4Url);
    } else {
      url = null;
    }
  } else if (hasValidSrc) {
    url = getProcessedSrc(src);
  } else {
    url = null;
  }

  // Determine fallback URL (only use mp4Url as fallback if we're using streamingUrl as primary)
  const fallbackSrc = hasValidStreamingUrl && hasValidMp4Url
    ? getProcessedSrc(video.mp4Url)
    : null;

  const texture = useVideoTexture(url, {
    crossOrigin: "anonymous",
    fallbackSrc,
    hls: {
      abrEwmaFastLive: 1.0,
      abrEwmaSlowLive: 3.0,
      enableWorker: true,
    },
  });
  return children(texture);
}

function WithImageRefTexture({ imgRef, children }) {
  const texture = useImageAsTexture(imgRef, {
    initTexture: true,
    premultiplyAlpha: "default",
  });
  return children(texture);
}

const NineGLImage = forwardRef((props, ref) => {
  const { tMap, isVideo, src, imgRef } = props;
  const gl = useThree((s) => s.gl);

  // Init texture to GPU only when tMap is passed
  // because useImageAsTexture already does this
  useEffect(
    function uploadTextureToGPU() {
      // console.log("NineGLImage", "uploadTextureToGPU()");
      tMap && gl.initTexture(tMap);
      //debug && console.log("useImageAsTexture", "initTexture()");
    },
    [gl, tMap]
  );

  if (tMap) {
    return <NineGLImageImpl ref={ref} {...props} tMap={tMap} />;
  }

  if (isVideo) {
    return (
      <WithVideoTexture src={src} video={props.video}>
        {(texture) => <NineGLImageImpl ref={ref} {...props} tMap={texture} />}
      </WithVideoTexture>
    );
  }

  return (
    <WithImageRefTexture imgRef={imgRef}>
      {(texture) => <NineGLImageImpl ref={ref} {...props} tMap={texture} />}
    </WithImageRefTexture>
  );
});

export default NineGLImage;
